import { useQuery } from '@tanstack/react-query';
import {
	BulkAccountLoader,
	VelocityClient,
	PublicKey,
} from '@velocity-exchange/sdk';
import { VaultClient } from '@velocity-exchange/vaults-sdk';
import { Connection } from '@solana/web3.js';
import { UiVaults } from 'src/constants/vaults';
import { ENABLE_STRATEGY_VAULTS } from 'src/constants/featureGates';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import {
	getMultipleOnChainVaultData,
	VaultsOnChainDataLookup,
} from 'src/hooks/vaults/vaultStatsSyncUtils';
import { VAULT_REFETCH_INTERVAL } from '../consts';
import { vaultQueryKeys } from '../vaultQueryKeys';

async function fetchNativeVaultOnChainData(
	velocityClient: VelocityClient,
	vaultClient: VaultClient,
	connection: Connection,
	bulkAccountLoader: BulkAccountLoader
): Promise<VaultsOnChainDataLookup> {
	const nativeVaults = UiVaults.getNativeVaults();
	const vaultPubkeys = nativeVaults.map(
		(vault) => new PublicKey(vault.vaultPubkeyString)
	);

	return getMultipleOnChainVaultData(
		velocityClient,
		vaultClient,
		connection,
		bulkAccountLoader,
		vaultPubkeys
	);
}

export function useVaultOnChainData() {
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const vaultClient = useVaultsStore((s) => s.vaultClient);
	const connection = useVelocityStore((s) => s.connection.current);
	const bulkAccountLoader = useVelocityStore((s) => s.connection.accountLoader);
	const areVaultConfigsLoaded = useVaultsStore((s) => s.areVaultConfigsLoaded);

	const isReady =
		ENABLE_STRATEGY_VAULTS &&
		velocityClientIsReady &&
		!!velocityClient &&
		!!vaultClient &&
		!!connection &&
		!!bulkAccountLoader &&
		areVaultConfigsLoaded;

	return useQuery({
		queryKey: vaultQueryKeys.vaultOnChainData(),
		queryFn: () =>
			fetchNativeVaultOnChainData(
				velocityClient!,
				vaultClient!,
				connection!,
				bulkAccountLoader!
			),
		enabled: isReady,
		staleTime: VAULT_REFETCH_INTERVAL,
		refetchInterval: VAULT_REFETCH_INTERVAL,
	});
}
